import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/features/shell/AppShell";
import { MailPage } from "@/features/mail/MailPage";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
// Not lazy: this page pulls in the Syncfusion Fluent2 stylesheets the whole app has always been styled with.
import { IdentitiesPage } from "@/features/settings/IdentitiesPage";
import { CallbackPage } from "@/auth/CallbackPage";
import { SilentRenewPage } from "@/auth/SilentRenewPage";

// Only the inbox is in the first download. Every other page is its own chunk (the settings pages carry the
// rich-text editor, People and Tasks have their own code), loaded the first time you open it.
const MessageFullViewLazy = lazy(() => import("@/features/mail/MessageFullView").then((m) => ({ default: m.MessageFullView })));
const PeoplePageLazy = lazy(() => import("@/features/people/PeoplePage").then((m) => ({ default: m.PeoplePage })));
const TasksPageLazy = lazy(() => import("@/features/tasks/TasksPage").then((m) => ({ default: m.TasksPage })));
const SettingsPageLazy = lazy(() => import("@/features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const GeneralPageLazy = lazy(() => import("@/features/settings/GeneralPage").then((m) => ({ default: m.GeneralPage })));
const NotificationsPageLazy = lazy(() => import("@/features/settings/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const MailClientPageLazy = lazy(() => import("@/features/settings/MailClientPage").then((m) => ({ default: m.MailClientPage })));
const LabelsPageLazy = lazy(() => import("@/features/settings/LabelsPage").then((m) => ({ default: m.LabelsPage })));
const FiltersPageLazy = lazy(() => import("@/features/settings/FiltersPage").then((m) => ({ default: m.FiltersPage })));
const AutoRespondersPageLazy = lazy(() => import("@/features/settings/AutoRespondersPage").then((m) => ({ default: m.AutoRespondersPage })));
const VacationPageLazy = lazy(() => import("@/features/settings/VacationPage").then((m) => ({ default: m.VacationPage })));
const TemplatesPageLazy = lazy(() => import("@/features/settings/TemplatesPage").then((m) => ({ default: m.TemplatesPage })));
const ShortcutsPageLazy = lazy(() => import("@/features/settings/ShortcutsPage").then((m) => ({ default: m.ShortcutsPage })));

/** Gives a lazy page its own fallback so the app shell stays on screen while it loads. */
function Lazy({ children }: { children: ReactNode }) {
    return <Suspense fallback={<div className="p-6 text-sm text-muted-foreground" role="status">Loading…</div>}>{children}</Suspense>;
}

// Calendar pulls in @syncfusion/ej2-react-schedule, a sizeable chunk.
// Route-level code-split keeps it out of the initial Mail bundle.
const CalendarPage = lazy(() =>
    import("@/features/calendar/CalendarPage").then((m) => ({ default: m.CalendarPage }))
);

function CalendarRoute() {
    return (
        <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading calendar…</div>}>
            <CalendarPage />
        </Suspense>
    );
}

// Wraps every signed-in route so an anonymous user is bounced to the
// Identity Service before the shell even mounts. The callback and silent
// renew routes stay outside the guard since they complete the sign-in.
function ProtectedShell() {
    return (
        <ProtectedRoute>
            <AppShell />
        </ProtectedRoute>
    );
}

export function App() {
    return (
        <>
            <Routes>
                <Route path="/auth/callback" element={<CallbackPage />} />
                <Route path="/auth/silent-renew" element={<SilentRenewPage />} />
                <Route element={<ProtectedShell />}>
                    <Route path="/" element={<Navigate to="/mail" replace />} />
                    <Route path="/mail" element={<MailPage />} />
                    {/* Full-window single message. Double-clicking a row opens
                        this in a new tab, which is the only way to read a long
                        message comfortably when the reading pane is stacked. */}
                    <Route path="/mail/message/:messageId" element={<Lazy><MessageFullViewLazy /></Lazy>} />
                    <Route path="/calendar" element={<CalendarRoute />} />
                    <Route path="/people" element={<Lazy><PeoplePageLazy /></Lazy>} />
                    <Route path="/tasks" element={<Lazy><TasksPageLazy /></Lazy>} />
                    <Route path="/settings" element={<Lazy><SettingsPageLazy /></Lazy>}>
                        <Route index element={<Navigate to="/settings/general" replace />} />
                        <Route path="general" element={<Lazy><GeneralPageLazy /></Lazy>} />
                        <Route path="notifications" element={<Lazy><NotificationsPageLazy /></Lazy>} />
                        <Route path="mail-client" element={<Lazy><MailClientPageLazy /></Lazy>} />
                        <Route path="identities" element={<IdentitiesPage />} />
                        <Route path="labels" element={<Lazy><LabelsPageLazy /></Lazy>} />
                        <Route path="filters" element={<Lazy><FiltersPageLazy /></Lazy>} />
                        <Route path="auto-responders" element={<Lazy><AutoRespondersPageLazy /></Lazy>} />
                        <Route path="vacation" element={<Lazy><VacationPageLazy /></Lazy>} />
                        <Route path="templates" element={<Lazy><TemplatesPageLazy /></Lazy>} />
                        <Route path="shortcuts" element={<Lazy><ShortcutsPageLazy /></Lazy>} />
                    </Route>
                    <Route path="*" element={<Navigate to="/mail" replace />} />
                </Route>
            </Routes>
        </>
    );
}
